import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canSeeCost, money, date as fmtDate, todayIE } from "@/lib/format";
import { PageHeader, Empty } from "@/components/Bits";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, FileBarChart } from "lucide-react";

function toCsv(rows, cols) {
  const head = cols.map((c) => c.label).join(",");
  const body = rows.map((r) => cols.map((c) => `"${String(r[c.key] ?? "")}"`).join(",")).join("\n");
  return head + "\n" + body;
}
function saveBlob(name, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const nowIE = () => new Date(todayIE() + "T12:00:00");
const PRESETS = {
  "This week": () => { const m = monday(nowIE()); const s = new Date(m); s.setDate(m.getDate() + 6); return [iso(m), iso(s)]; },
  "Last week": () => { const m = monday(nowIE()); m.setDate(m.getDate() - 7); const s = new Date(m); s.setDate(m.getDate() + 6); return [iso(m), iso(s)]; },
  "This month": () => { const n = nowIE(); return [iso(new Date(n.getFullYear(), n.getMonth(), 1)), iso(n)]; },
  "Last 30 days": () => { const n = nowIE(); const s = nowIE(); s.setDate(n.getDate() - 30); return [iso(s), iso(n)]; },
  "All time": () => ["", ""],
};

export default function Reports() {
  const { user } = useAuth();
  const cost = canSeeCost(user.role);
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Reports" subtitle="Download cost, hours and payroll reports as CSV or Excel" />
      <Tabs defaultValue={cost ? "centre" : "job"}>
        <TabsList className="flex-wrap h-auto">
          {cost && <TabsTrigger value="centre" data-testid="tab-report-centre">Report Centre</TabsTrigger>}
          <TabsTrigger value="job" data-testid="tab-report-job">Hours by Job</TabsTrigger>
          <TabsTrigger value="client" data-testid="tab-report-client">Hours by Client</TabsTrigger>
        </TabsList>
        {cost && <TabsContent value="centre"><ReportCentre /></TabsContent>}
        <TabsContent value="job"><HoursReport group="job" /></TabsContent>
        <TabsContent value="client"><HoursReport group="client" /></TabsContent>
      </Tabs>
    </div>
  );
}

function ReportCentre() {
  const [catalog, setCatalog] = useState([]);
  const [key, setKey] = useState("cost-per-client");
  const [range, setRange] = useState(PRESETS["This month"]());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [offOnly, setOffOnly] = useState(false);
  useEffect(() => { api.get("/reports/catalog").then((r) => setCatalog(r.data)); }, []);
  const params = { start: range[0], end: range[1], ...(key === "attendance" && offOnly ? { off_site_only: true } : {}) };
  const run = useCallback(async () => {
    setBusy("json");
    try { const { data: d } = await api.get(`/reports/run/${key}`, { params: { start: range[0], end: range[1], ...(key === "attendance" && offOnly ? { off_site_only: true } : {}) } }); setData(d); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  }, [key, range, offOnly]);
  useEffect(() => { run(); }, [run]);
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const r = await api.get(`/reports/run/${key}`, { params: { ...params, fmt }, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `MK-${key}.${fmt}`;
      saveBlob(name, r.data); toast.success(`Downloaded ${name}`);
    } catch (e) { toast.error("Download failed"); } finally { setBusy(""); }
  };
  const current = catalog.find((c) => c.key === key);

  return (
    <div className="mt-4 grid gap-5 lg:grid-cols-[280px_1fr]">
      <div className="space-y-2" data-testid="report-catalog">
        {catalog.map((c) => (
          <button key={c.key} onClick={() => setKey(c.key)} data-testid={`report-pick-${c.key}`}
            className={`w-full text-left p-3 rounded-lg border transition-colors ${key === c.key ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
            <div className="flex items-center gap-2 font-heading font-bold text-sm"><FileBarChart className="w-4 h-4 text-primary" />{c.label}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{c.desc}</div>
          </button>
        ))}
      </div>
      <div className="min-w-0">
        <div className="card-industrial p-4 mb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">{key === "payroll" ? "Week containing" : "From"}</Label>
              <Input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} data-testid="report-start" className="mt-1 w-40" /></div>
            {key !== "payroll" && <div><Label className="text-xs">To</Label>
              <Input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} data-testid="report-end" className="mt-1 w-40" /></div>}
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(PRESETS).map((p) => (
                <button key={p} onClick={() => setRange(PRESETS[p]())} data-testid={`report-preset-${p.replace(/\s/g, "-").toLowerCase()}`}
                  className="text-xs px-2.5 py-1.5 rounded-md bg-accent hover:bg-accent/70">{p}</button>
              ))}
            </div>
            {key === "attendance" && (
              <label className="flex items-center gap-2 text-xs font-semibold h-9"><input type="checkbox" checked={offOnly} onChange={(e) => setOffOnly(e.target.checked)} data-testid="report-offsite-only" className="w-4 h-4 accent-orange-500" />Off-site only</label>
            )}
            <div className="flex gap-2 ml-auto">
              <Button size="sm" variant="outline" onClick={() => download("csv")} disabled={!!busy} data-testid="report-download-csv">
                {busy === "csv" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}CSV</Button>
              <Button size="sm" onClick={() => download("xlsx")} disabled={!!busy} data-testid="report-download-xlsx">
                {busy === "xlsx" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}Excel</Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2" data-testid="report-range-note">
            {current?.range}{data?.meta?.week_start ? ` · ${fmtDate(data.meta.week_start)} – ${fmtDate(data.meta.week_end)}` : ""}
          </p>
        </div>
        {busy === "json" && !data ? <div className="text-muted-foreground text-sm">Loading…</div> : <ReportTable data={data} />}
      </div>
    </div>
  );
}

const fmtCell = (v, t) => (t === "money" ? money(v) : t === "hours" ? `${v}h` : t === "days" ? `${v}d` : t === "pct" ? `${v}%` : t === "date" ? fmtDate(v) : v);

function ReportTable({ data }) {
  if (!data) return null;
  if (data.rows.length === 0) return <Empty text="No data for this period." />;
  const num = (t) => ["money", "hours", "pct", "int", "days"].includes(t);
  return (
    <div className="card-industrial overflow-x-auto" data-testid="report-table">
      <table className="w-full text-sm whitespace-nowrap">
        <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
          <tr>{data.columns.map((c) => <th key={c.key} className={`px-3 py-3 ${num(c.type) ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.rows.map((r, i) => (
            <tr key={i} data-testid={`report-centre-row-${i}`}>
              {data.columns.map((c) => <td key={c.key} className={`px-3 py-2.5 ${num(c.type) ? "text-right font-mono-num" : ""} ${c.key === "margin" && r[c.key] < 0 ? "text-red-400" : ""}`}>{fmtCell(r[c.key], c.type)}</td>)}
            </tr>
          ))}
          <tr className="bg-accent/30 font-bold" data-testid="report-totals-row">
            {data.columns.map((c, i) => <td key={c.key} className={`px-3 py-3 ${num(c.type) ? "text-right font-mono-num" : ""}`}>{i === 0 ? "TOTAL" : data.totals[c.key] != null ? fmtCell(data.totals[c.key], c.type) : ""}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function HoursReport({ group }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reports/hours", { params: { group } }).then((r) => setRows(r.data.rows)); }, [group]);
  const cols = group === "job"
    ? [{ key: "job_number", label: "Job" }, { key: "job_name", label: "Job name" }, { key: "customer_name", label: "Client" }, { key: "job_type", label: "Type" }, { key: "worked_hours", label: "Worked" }, { key: "billed_hours", label: "Billed" }, { key: "variance_hours", label: "Variance" }]
    : [{ key: "customer_name", label: "Client" }, { key: "jobs", label: "Jobs" }, { key: "worked_hours", label: "Worked" }, { key: "billed_hours", label: "Billed" }, { key: "variance_hours", label: "Variance" }];

  if (rows.length === 0) return <Empty text="No data." />;
  return (
    <div className="mt-4">
      <div className="flex justify-end mb-2">
        <Button size="sm" variant="outline" onClick={() => saveBlob(`hours-by-${group}.csv`, new Blob([toCsv(rows, cols)], { type: "text/csv" }))} data-testid="report-export"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
      </div>
      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <tr>{cols.map((c) => <th key={c.key} className={`px-4 py-3 ${["worked_hours", "billed_hours", "variance_hours", "jobs"].includes(c.key) ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={i} data-testid={`report-row-${i}`}>
                {cols.map((c) => {
                  const n = ["worked_hours", "billed_hours", "variance_hours", "jobs"].includes(c.key);
                  const isJob = c.key === "job_number";
                  return (
                    <td key={c.key} className={`px-4 py-3 ${n ? "text-right font-mono-num" : ""} ${isJob ? "text-primary font-bold" : ""} ${c.key === "variance_hours" && r[c.key] > 0 ? "text-amber-400 font-bold" : ""}`}>
                      {isJob ? <Link to={`/jobs/${r.job_id}`} className="hover:underline">{r[c.key]}</Link> : (n ? `${r[c.key]}${c.key === "jobs" ? "" : "h"}` : r[c.key])}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
